/**
 * ClusterItemSelector Component
 *
 * Tree-based multi-select for selecting regions, countries, and locations
 * to add to a cluster. Displays hierarchical structure with checkboxes.
 */

import { useState, useMemo, useEffect } from 'react';
import { ChevronRight, ChevronDown, Globe2, Flag, MapPin, Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Checkbox } from '@/components/ui/Checkbox';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/Badge';
import type { Region, Country, Location, ClusterItem, ClusterItemType } from '@simcheck/shared-types';

export interface ClusterItemSelectorProps {
  regions: Region[];
  countries: Country[];
  locations: Location[];
  selectedItems: ClusterItem[];
  onSelectionChange: (items: ClusterItem[]) => void;
  className?: string;
  maxHeight?: string;
}

interface TreeNode {
  id: string;
  type: ClusterItemType;
  name: string;
  children: TreeNode[];
  modemCount?: number;
  status?: string;
}

export function ClusterItemSelector({
  regions,
  countries,
  locations,
  selectedItems,
  onSelectionChange,
  className,
  maxHeight = '400px',
}: ClusterItemSelectorProps) {
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // Build tree structure
  const tree = useMemo(() => {
    const activeLocations = locations.filter(l => l.status === 'active');

    return regions.map((region): TreeNode => ({
      id: region.id,
      type: 'region',
      name: region.name,
      children: countries
        .filter(c => c.regionId === region.id)
        .map((country): TreeNode => ({
          id: country.id,
          type: 'country',
          name: country.name,
          children: activeLocations
            .filter(l => l.countryId === country.id)
            .map((location): TreeNode => ({
              id: location.id,
              type: 'location',
              name: location.displayName || location.name,
              children: [],
              modemCount: location.modemCount || 0,
              status: location.status,
            })),
        })),
    }));
  }, [regions, countries, locations]);

  // Helper to check if an item is selected
  const isItemSelected = (id: string, type: ClusterItemType): boolean => {
    return selectedItems.some(item => item.id === id && item.type === type);
  };

  // Helper to get all descendant items
  const getDescendants = (node: TreeNode): ClusterItem[] => {
    const descendants: ClusterItem[] = [];

    const traverse = (n: TreeNode) => {
      n.children.forEach(child => {
        descendants.push({ id: child.id, type: child.type, displayName: child.name });
        traverse(child);
      });
    };

    traverse(node);
    return descendants;
  };

  // Helper to check if any descendant is selected
  const hasSelectedDescendant = (node: TreeNode): boolean => {
    return node.children.some(child =>
      isItemSelected(child.id, child.type) || hasSelectedDescendant(child)
    );
  };

  // Check if all children are selected
  const areAllChildrenSelected = (node: TreeNode): boolean => {
    if (node.children.length === 0) return false;
    return node.children.every(child =>
      isItemSelected(child.id, child.type) || areAllChildrenSelected(child)
    );
  };

  // Get selection state for checkbox display
  const getSelectionState = (node: TreeNode): 'checked' | 'indeterminate' | 'unchecked' => {
    if (isItemSelected(node.id, node.type)) return 'checked';
    if (hasSelectedDescendant(node)) return 'indeterminate';
    return 'unchecked';
  };

  // Toggle node selection
  const toggleSelection = (node: TreeNode) => {
    const isSelected = isItemSelected(node.id, node.type);

    if (isSelected) {
      // Deselect this item and all descendants
      const descendantIds = getDescendants(node).map(d => `${d.type}:${d.id}`);
      const nodePath = `${node.type}:${node.id}`;
      const newItems = selectedItems.filter(item =>
        item.id !== node.id || item.type !== node.type
      ).filter(item =>
        !descendantIds.includes(`${item.type}:${item.id}`)
      );
      onSelectionChange(newItems);
    } else {
      // Select this item, deselect any selected descendants (avoid redundancy)
      const descendantIds = getDescendants(node).map(d => `${d.type}:${d.id}`);
      const newItems = selectedItems.filter(item =>
        !descendantIds.includes(`${item.type}:${item.id}`)
      );
      newItems.push({ id: node.id, type: node.type, displayName: node.name });
      onSelectionChange(newItems);
    }
  };

  // Toggle expand/collapse
  const toggleExpand = (nodeId: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(nodeId)) {
      newExpanded.delete(nodeId);
    } else {
      newExpanded.add(nodeId);
    }
    setExpandedNodes(newExpanded);
  };

  // Expand all nodes with selections
  useEffect(() => {
    if (selectedItems.length > 0) {
      const toExpand = new Set<string>();

      selectedItems.forEach(item => {
        if (item.type === 'location') {
          const location = locations.find(l => l.id === item.id);
          if (location) {
            toExpand.add(location.countryId);
            const country = countries.find(c => c.id === location.countryId);
            if (country) {
              toExpand.add(country.regionId);
            }
          }
        } else if (item.type === 'country') {
          const country = countries.find(c => c.id === item.id);
          if (country) {
            toExpand.add(country.regionId);
          }
        }
      });

      setExpandedNodes(prev => new Set([...prev, ...toExpand]));
    }
  }, [selectedItems, locations, countries]);

  const renderNode = (node: TreeNode, level: number = 0) => {
    const isExpanded = expandedNodes.has(node.id);
    const hasChildren = node.children.length > 0;
    const selectionState = getSelectionState(node);

    const icons: Record<ClusterItemType, typeof Globe2> = {
      region: Globe2,
      country: Flag,
      location: MapPin,
    };

    const Icon = icons[node.type];

    return (
      <div key={`${node.type}:${node.id}`}>
        <div
          className={cn(
            'flex items-center gap-2 py-1.5 px-2 rounded hover:bg-muted cursor-pointer',
            selectionState === 'checked' && 'bg-accent/50'
          )}
          style={{ paddingLeft: `${level * 20 + 8}px` }}
        >
          {/* Expand/collapse button */}
          {hasChildren ? (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(node.id);
              }}
              className="w-5 h-5 flex items-center justify-center text-muted-foreground hover:text-foreground"
            >
              {isExpanded ? (
                <ChevronDown className="w-4 h-4" />
              ) : (
                <ChevronRight className="w-4 h-4" />
              )}
            </button>
          ) : (
            <span className="w-5" />
          )}

          {/* Checkbox */}
          <div
            onClick={() => toggleSelection(node)}
            className="flex items-center gap-2 flex-1"
          >
            <Checkbox
              checked={selectionState === 'checked'}
              className={cn(
                selectionState === 'indeterminate' && 'data-[state=checked]:bg-primary/50'
              )}
              onCheckedChange={() => toggleSelection(node)}
            />

            {/* Icon */}
            <Icon className="w-4 h-4 text-muted-foreground" />

            {/* Name */}
            <span className="text-sm flex-1">{node.name}</span>

            {/* Stats */}
            {node.type === 'location' && node.modemCount !== undefined && (
              <Badge variant="secondary" className="text-xs">
                {node.modemCount} modem{node.modemCount !== 1 ? 's' : ''}
              </Badge>
            )}

            {node.type === 'region' && (
              <span className="text-xs text-muted-foreground">
                {node.children.length} countries
              </span>
            )}

            {node.type === 'country' && (
              <span className="text-xs text-muted-foreground">
                {node.children.length} locations
              </span>
            )}
          </div>
        </div>

        {/* Children */}
        {hasChildren && isExpanded && (
          <div>
            {node.children.map(child => renderNode(child, level + 1))}
          </div>
        )}
      </div>
    );
  };

  // Calculate stats
  const stats = useMemo(() => {
    const regionCount = selectedItems.filter(i => i.type === 'region').length;
    const countryCount = selectedItems.filter(i => i.type === 'country').length;
    const locationCount = selectedItems.filter(i => i.type === 'location').length;
    return { regionCount, countryCount, locationCount };
  }, [selectedItems]);

  return (
    <div className={cn('border rounded-lg', className)}>
      {/* Header with stats */}
      <div className="p-3 border-b bg-muted/50">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Select Items</span>
          <div className="flex items-center gap-2">
            {stats.regionCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                <Globe2 className="w-3 h-3 mr-1" />
                {stats.regionCount} region{stats.regionCount !== 1 ? 's' : ''}
              </Badge>
            )}
            {stats.countryCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                <Flag className="w-3 h-3 mr-1" />
                {stats.countryCount} countr{stats.countryCount !== 1 ? 'ies' : 'y'}
              </Badge>
            )}
            {stats.locationCount > 0 && (
              <Badge variant="secondary" className="text-xs">
                <MapPin className="w-3 h-3 mr-1" />
                {stats.locationCount} location{stats.locationCount !== 1 ? 's' : ''}
              </Badge>
            )}
          </div>
        </div>
      </div>

      {/* Tree */}
      <ScrollArea style={{ maxHeight }}>
        <div className="p-2">
          {tree.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No regions available
            </div>
          ) : (
            tree.map(node => renderNode(node))
          )}
        </div>
      </ScrollArea>
    </div>
  );
}

export default ClusterItemSelector;
